/**
 * Select
 */
import { findIndex, has, isEqual, isArray, differenceWith } from 'lodash';
import { Media, FilterBuilder } from 'ca-api-client';

// state
const initialState = () => ({
    items: {
        media: {
            meta: {},
            data: [],
        },
        sets: {
            meta: {},
            data: [],
        },
    },
    media: {
        fetchOptions: {
            limit: 10,
            sort: '-created_at',
            direction: 'ASC',
            page: 1,
        },
        meta: {},
    },
    selectedItems: [],
    selectedItemCount: 0,
    currentSelection: null,
    currentFilter: [],
    currentFilterApplied: false,
    isLoading: false,
    strategyManager: null,
    strategy: {},
    sidebar: 'Default',
    validator: null,
    actionCalled: false,
    options: {
        title: 'Select media',
        description: '',
        type: null,
        selectMin: 1,
        selectMax: null,
        selectExact: null,
        mediaTypes: null,
        onUseSelected: null,
        sidebar: true,
        upload: {
            allow: false,
            allowMultiple: false,
            allowedFileTypes: null,
        },
    },
});

export const selectState = {
    select: {
        ...initialState(),
    },
};

// mutations
export const selectMutations = {
    /**
     * Sets media select options
     * @param {object} options
     * @see https://github.com/spatialmedia/cms/wiki/MediaSelectModal
     */
    setMediaSelectOptions(state, options) {
        state.select.options = options;
    },

    /**
     * Sets media select media items
     * @param {object} items - Media
     */
    setMediaSelectItems(state, items) {
        state.select.items = items;
    },

    /**
     * Sets media select media items
     * @param {object} items - Media
     */
    setMediaSelectMediaItems(state, items) {
        state.select.items.media = { data: items };
    },

    /**
     * Sets media select sidebar
     * Currently on Default is used, in future strategies can define custom sidebars
     * @param {string} sidebar - Sidebar component name
     */
    setMediaSelectSidebar(state, sidebar) {
        state.select.sidebar = sidebar;
    },

    /**
     * Adds media item to selected items
     * @param {string} itemId - Media item id
     */
    addMediaSelectSelectedItem(state, item) {
        state.select.selectedItems.push(item);
    },

    /**
     * Sets media select selected items array
     * @param {array} items - An array of Media objects
     */
    setMediaSelectSelectedItems(state, items) {
        state.select.selectedItems = items;
        state.select.selectedItemCount = items.length;
    },

    /**
     * Sets media select selected item count
     * @param {number} count - Length of selectedItems
     */
    setMediaSelectSelectedItemCount(state, count) {
        state.select.selectedItemCount = count;
    },

    /**
     * Stores media select last selection
     * @param {object} item - Media
     */
    setMediaSelectCurrentSelection(state, item) {
        state.select.currentSelection = item;
    },

    /**
     * Sets media select loading state
     * @param {boolean} loading
     */
    setMediaSelectIsLoading(state, loading) {
        state.select.isLoading = loading;
    },

    /**
     * Sets media select fetch metadata
     * @param {object} meta - meatadta retured in a api request
     */
    setMediaSelectItemsMeta(state, meta) {
        state.select.media.meta = meta;
    },

    /**
     * Sets media select strategy manager class
     * @param {object} manager - instance of ConfigStrategyManager
     */
    setMediaSelectStrategyManager(state, manager) {
        state.select.strategyManager = manager;
    },

    /**
     * Sets media select strategy manager class
     * @param {object} manager - instance of ConfigStrategyManager
     */
    setMediaSelectStrategy(state, config) {
        state.select.strategy = config;
    },

    /**
     * Sets media select validator object
     * @param {object} validator - instance of Vuelidate (this.$v)
     */
    setMediaSelectValidator(state, validator) {
        state.select.validator = validator;
    },

    /**
     * Sets media select action was called
     * This value is globally watched, when true will trigger the media select
     * to call the strategies callback function
     * @param {boolean} called
     */
    setMediaSelectActionCalled(state, called) {
        state.select.actionCalled = called;
    },

    /**
     * Sets a filter for media select media items
     * @param {payload} filter
     */
    setMediaSelectFilter(state, payload) {
        const mode = payload.mode;
        const isChecked = payload.checked ? true : false;
        const currentFilter = state.select.currentFilter;
        const index = findIndex(currentFilter, payload.filter);
        const textFilterIndex = findIndex(currentFilter, 'text');

        if ((textFilterIndex === -1 && index === -1) || (index === -1 && isChecked)) {
            currentFilter.push(payload.filter);
        }

        if (textFilterIndex === -1 && index !== -1 && !isChecked) {
            currentFilter.splice(index, 1);
        }

        if (textFilterIndex !== -1 && index === -1 && !isChecked) {
            if (payload.filter.text) {
                currentFilter.splice(textFilterIndex, 1, payload.filter);
            } else {
                currentFilter.splice(textFilterIndex, 1);
            }
        }
    },

    /**
     * Sets the current filter for media select
     * @param {filter} array - Array of filter objects
     * Eg. [{type: 'image'}, {360: true}]
     */
    setMediaSelectCurrentFilter(state, filter) {
        if (filter.length === 0) {
            const config = state.select.strategyManager.config();
            if (has(config, 'defaultFilters')) {
                filter = config.defaultFilters;
            }
        }
        console.log('SETTING CURRENT FILTER', filter);
        state.select.currentFilter = filter;
    },

    /**
     * Sets media select filters applied status
     * @returns {boolean}
     */
    setMediaSelectFilterApplied(state, applied) {
        return (state.select.currentFilterApplied = applied);
    },
};

// getters
export const selectGetters = {
    /**
     * Gets media select options
     * @see https://github.com/spatialmedia/cms/wiki/MediaSelectModal
     * @returns {object}
     */
    getMediaSelectOptions(state) {
        return state.select.options;
    },

    /**
     * Gets media select items
     * @returns {array} - Media
     */
    getMediaSelectItems(state) {
        return state.select.items;
    },

    /**
     * Gets media select media items
     * @returns {array} - Media
     */
    getMediaSelectMediaItems(state) {
        let data = [];
        if (has(state.select.items.media, 'data')) {
            data = state.select.items.media.data;
        }
        return data;
    },

    /**
     * Gets media select fetch metadata
     * @returns {object}
     */
    getMediaSelectItemsMeta(state) {
        return state.select.items.media.meta;
    },

    /**
     * Gets media select sidebar component name
     * @returns {string}
     */
    getMediaSelectSidebar(state) {
        return state.select.sidebar;
    },

    /**
     * Gets media select selected items array
     * @returns {array}
     */
    getMediaSelectSelected(state) {
        return state.select.selectedItems;
    },

    getMediaSelectSelectedByType(state) {
        const selectedTypes =  {};
        const setTypes = ['set', 'gallery'];
        const selected = state.select.selectedItems;
        const media = selected.filter(i => !setTypes.includes(i.type)).map(i => i.id);
        const sets = selected.filter(i => setTypes.includes(i.type)).map(i => i.id);

        if (media.length > 0) {
            selectedTypes.media = media;
        }

        if (sets.length > 0) {
            selectedTypes.sets = sets;
        }

        return selectedTypes;
    },

    /**
     * Gets media select selected items length
     * @returns {number}
     */
    getMediaSelectSelectedCount(state) {
        return state.select.selectedItemCount;
    },

    /**
     * Gets media select selected last selected item
     * @returns {object} - Media
     */
    getMediaSelectCurrentSelection(state) {
        return state.select.currentSelection;
    },

    /**
     * Gets media select select loading state
     * @returns {boolean}
     */
    getMediaSelectIsLoading(state) {
        return state.select.isLoading;
    },

    /**
     * Gets media select item by it's id
     * @returns {object} - Media
     */
    getMediaSelectItemById: (state) => (id) => {
        const items = state.select.items;
        const media = items.media;
        let mediaFound = media.data.filter((item) => item.id === id);
        mediaFound = mediaFound.length > 0 ? mediaFound.pop() : false;

        let setsFound = false;
        if (has(items, 'sets')) {
            setsFound = items.sets.data.filter((item) => item.id === id);
            setsFound = setsFound.length > 0 ? setsFound.pop() : false;
        }

        return mediaFound || setsFound;
    },

    /**
     * Gets media selects strategy manager
     * @returns {object} - Instance of ConfigStrategyManager
     */
    getMediaSelectStrategyManager(state) {
        return state.select.strategyManager;
    },

    /**
     * Gets media selects strategy
     * @returns {object} - Instance of strategy
     */
    getMediaSelectStrategy(state) {
        return state.select.strategy;
    },

    /**
     * Gets media selects validator
     * @returns {object} - Instance of Vuelidate (this.$v)
     */
    getMediaSelectValidator(state) {
        return state.select.validator;
    },

    /**
     * Gets media selects actionCalled state
     * @returns {boolean}
     */
    getMediaSelectActionCalled(state) {
        return state.select.actionCalled;
    },

    /**
     * Gets media select current filters
     * @returns {array} - An array of api filters
     */
    getMediaSelectCurrentFilter(state) {
        return state.select.currentFilter;
    },

    /**
     * Gets media select current fetch options
     * @returns {object}
     */
    getMediaSelectFetchOptions(state) {
        return {
            limit: state.select.media.fetchOptions.limit,
            direction: state.select.media.fetchOptions.direction,
            sort: state.select.media.fetchOptions.sort,
        };
    },

    /**
     * Gets media select default filters
     * @returns {array} - An array of api filters
     */
    getMediaSelectDefaultFilter(state) {
        let filters = [];
        const config = state.select.strategyManager.config();
        if (has(config, 'defaultFilters')) {
            filters = config.defaultFilters;
        }
        return filters;
    },

    /**
     * Gets media select filters dirty status
     * @returns {boolean}
     */
    getMediaSelectFilterDirty(state) {
        let dirty = false;
        const config = state.select.strategyManager.config();
        if (state.select.currentFilter.length > 0 && has(config, 'defaultFilters')) {
            dirty = !isEqual(state.select.currentFilter, config.defaultFilters);
        }

        return dirty;
    },

    /**
     * Gets media select filters applied status
     * @returns {boolean}
     */
    getMediaSelectFilterApplied(state) {
        return state.select.currentFilterApplied;
    },

    /**
     * Gets media select filters count
     * @returns {number}
     */
    getMediaSelectFilterCount(state) {
        let count = 0;
        const config = state.select.strategyManager.config();
        if (has(config, 'defaultFilters') && !isEqual(state.select.currentFilter, config.defaultFilters)) {
            const diff = differenceWith(state.select.currentFilter, config.defaultFilters, isEqual);
            count = diff.length;
        }
        return count;
    },
};

// actions
export const selectActions = {
    /**
     * Adds media items to media select, sets fetch metadata
     * @param {object} response - XHR api response
     * @return {object} Promise
     */
    addMediaSelectItems({ commit, state }, response) {
        const items = {};
        if (has(response, 'media')) {
            items.media = response.media;
        }
        if (has(response, 'sets')) {
            items.sets = response.sets;
        }
        commit('setMediaSelectItems', items);
    },

    /**
     * Adds an item to media select
     * In single mode just replaces the item, in mutiple mode adds it if it doens't exist
     * @param {object} item - Media
     * @return {object} Promise
     */
    addMediaSelectItem({ commit, getters, state }, item) {
        const selected = state.select.selectedItems.map(i => i.id);
        const exists = selected.includes(item.id);

        switch (item.mode) {
            case 'multiple':
                if (item.checked && !exists) {
                    commit('addMediaSelectSelectedItem', item);
                } else {
                    const filteredArray = state.select.selectedItems.filter(e => e.id !== item.id);
                    commit('setMediaSelectSelectedItems', filteredArray);
                }
                break;

            case 'single':
                if (item.checked && !exists) {
                    commit('setMediaSelectSelectedItems', [item]);
                } else {
                    commit('setMediaSelectSelectedItems', []);
                }
                break;
        }

        // set currently selected item
        commit('setMediaSelectCurrentSelection', getters.getMediaSelectItemById(item.id));
        // update selected count
        commit('setMediaSelectSelectedItemCount', state.select.selectedItems.length);
    },

    /**
     * Prepends an media item to media select
     * @param {object} item - Media
     * @return {object} Promise
     */
    prependGalleryMediaSelectItem({ commit, state }, item) {
        return new Promise((resolve) => {
            state.select.items.media.data.unshift(item);
            resolve(item);
        });
    },

    /**
     * Prepends or replaces an media item to media select
     * @param {object} media - Media
     * @return {object} Promise
     */
    prependOrReplaceMediaSelectItem({ state }, media) {
        return new Promise((resolve) => {
            const itemIndex = state.select.items.media.data.findIndex((item) => item.id === media.id);

            if (itemIndex > -1) {
                state.select.items.media.data.splice(itemIndex, 1, media);
            } else {
                state.select.items.media.data.unshift(media);
            }
            resolve();
        });
    },

    /**
     * Resets media select to original state
     * @see initialState()
     * @return {object} Promise
     */
    resetMediaSelect({ state }) {
        state.select = Object.assign(
            {},
            {
                ...initialState(),
            }
        );
    },

    /**
     * Fetches media select media items fro api
     * @see https://github.com/robsontenorio/vue-api-query for availbale options
     * @see ca-api docs Media
     * @return {object} Promise
     */
    async fetchMediaSelectItems({ commit, dispatch, getters }, payload) {
        const result = {};

        //if (payload.media) {
        let params = {
            limit: payload.limit,
            sort: payload.sort,
            filter: { project_id: ca.project.uuid },
        };

        if (payload.page > 0) params.page = payload.page;

        if (payload.include) {
            const includes = params.include.split(',');
            if (!includes.includes(payload.include)) includes.push(payload.include);
            params.include = includes.join(',');
        }

        // add in payload filters if present
        if (has(payload, 'filters') && isArray(payload.filters) && payload.filters.length > 0) {
            const fb = new FilterBuilder();

            payload.filters.forEach((filter) => {
                try {
                    fb.filter(filter);
                } catch (err) {
                    console.log('filter builder error', filter);
                }
            });

            console.log('BUILD FILTERS', fb);

            params = Object.assign({}, params, fb.build());
        }

        // do media api query
        const media = await Media.params(params).get();
        result.media = media;

        // sets have been requested add them in
        if (payload.sets) {
            const filter = { project_id: ca.project.uuid, type: 'gallery' };

            if (has(payload, 'filters') && isArray(payload.filters) && payload.filters.length > 0) {
                const textSearchIndex = findIndex(payload.filters, 'text');
                if (textSearchIndex !== -1) {
                    filter.text = payload.filters[textSearchIndex]['text'];
                }
            }

            const sets = await dispatch('fetchMediaSets', {
                sort: payload.sort,
                include: 'children',
                filter,
            });

            result.sets = sets;
        }

        return result;
    },

    async fetchMediaSelectMediaItems({ commit, dispatch, getters }, payload) {
        let params = {
            limit: payload.limit,
            sort: payload.sort,
            filter: { project_id: ca.project.uuid },
        };

        if (payload.page > 0) params.page = payload.page;

        if (payload.include) {
            const includes = params.include.split(',');
            if (!includes.includes(payload.include)) includes.push(payload.include);
            params.include = includes.join(',');
        }

        // add in payload filters if present
        if (payload.filters && Array.isArray(payload.filters) && payload.filters.length > 0) {
            const fb = new FilterBuilder();

            payload.filters.forEach((filter, index) => {
                Object.keys(filter).forEach((key) => {
                    if (payload.filters[index][key] !== null) fb.filter(filter);
                });
            });

            params = Object.assign({}, params, fb.build());
        }

        const media = await Media.params(params).get();

        const existing = getters.getMediaSelectMediaItems;
        const data = existing.concat(media.data);

        dispatch('addMediaSelectItems', { media: { data, meta: media.meta } });

        return media;
    },

    /**
     * Resets the media select filters
     * @return {object} Promise
     */
    resetMediaSelectFilters({ commit, getters }) {
        let filters = [];
        const defaultFilter = getters.getMediaSelectDefaultFilter;
        if (defaultFilter) {
            filters = defaultFilter;
        }
        commit('setMediaSelectCurrentFilter', filters);
        commit('setMediaSelectFilterApplied', false);
    },

    /**
     * Removes a media select filter from filter array
     * @param {object} filter
     * Eg. {type: 'image}
     * @return {object} Promise
     */

    removeMediaSelectFilter({ state, getters }, filter = false) {
        const currentFilter = state.select.currentFilter;

        if (filter) {
            let index = findIndex(currentFilter, filter.value);

            if (index !== -1) {
                state.select.currentFilter.splice(index, 1);
            }
        }
    },

    clearMediaSelectSelected({ commit }) {
        commit('setMediaSelectSelectedItems', []);
    },
};
